import { useMemo } from "react";
import {
  TrainingGroupRef,
  TrainingPeriodTree,
  useTrainingCohortTree,
} from "../../api/trainingCohorts";
import { formatTitleCaseTr } from "../../utils/textCase";
import "./CohortFilterBar.css";

export type CohortFilterValue = {
  periodId: string;
  groupId: string;
  subgroupId: string;
};

type Props = {
  value: CohortFilterValue;
  onChange: (value: CohortFilterValue) => void;
  className?: string;
  disabled?: boolean;
};

function findPeriod(tree: TrainingPeriodTree[], periodId: string) {
  return tree.find((p) => p.id === periodId);
}

function findGroup(period: TrainingPeriodTree | undefined, groupId: string): TrainingGroupRef | undefined {
  return period?.groups.find((g) => g.id === groupId);
}

export function CohortFilterBar({ value, onChange, className = "", disabled = false }: Props) {
  const { tree, loading, error } = useTrainingCohortTree();

  const selectedPeriod = useMemo(
    () => findPeriod(tree, value.periodId),
    [tree, value.periodId],
  );
  const selectedGroup = useMemo(
    () => findGroup(selectedPeriod, value.groupId),
    [selectedPeriod, value.groupId],
  );

  const setPeriod = (periodId: string) => {
    onChange({ periodId, groupId: "", subgroupId: "" });
  };

  const setGroup = (groupId: string) => {
    onChange({ ...value, groupId, subgroupId: "" });
  };

  const setSubgroup = (subgroupId: string) => {
    onChange({ ...value, subgroupId });
  };

  return (
    <div className={`kp-cohort-filter ${className}`.trim()}>
      <select
        className="kp-input"
        value={value.periodId}
        disabled={disabled || loading}
        onChange={(e) => setPeriod(e.target.value)}
        aria-label="Kayit donemi"
      >
        <option value="">Tüm Dönemler</option>
        {tree.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
      <select
        className="kp-input"
        value={value.groupId}
        disabled={disabled || loading || !value.periodId}
        onChange={(e) => setGroup(e.target.value)}
        aria-label="Grup"
      >
        <option value="">Tüm Gruplar</option>
        {selectedPeriod?.groups.map((g) => (
          <option key={g.id} value={g.id}>
            {formatTitleCaseTr(g.label)}
          </option>
        ))}
      </select>
      <select
        className="kp-input"
        value={value.subgroupId}
        disabled={disabled || loading || !value.groupId}
        onChange={(e) => setSubgroup(e.target.value)}
        aria-label="Alt grup"
      >
        <option value="">Tüm Alt Gruplar</option>
        {selectedGroup?.subgroups.map((sg) => (
          <option key={sg.id} value={sg.id}>
            {formatTitleCaseTr(sg.label)}
            {sg.member_count > 0 ? ` (${sg.member_count})` : ""}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="kp-btn kp-btn--ghost"
        disabled={disabled || (!value.periodId && !value.groupId && !value.subgroupId)}
        onClick={() => onChange({ periodId: "", groupId: "", subgroupId: "" })}
      >
        Temizle
      </button>
      {error && <span className="kp-cohort-filter__error">{error}</span>}
    </div>
  );
}

export function cohortFilterSummary(
  tree: TrainingPeriodTree[],
  value: CohortFilterValue,
): string | null {
  if (!value.periodId && !value.groupId && !value.subgroupId) return null;
  const period = findPeriod(tree, value.periodId);
  const group = findGroup(period, value.groupId);
  const sg = group?.subgroups.find((s) => s.id === value.subgroupId);
  if (sg && group && period) return `${period.label} · ${group.label} · ${sg.label}`;
  if (group && period) return `${period.label} · ${group.label}`;
  if (period) return period.label;
  return null;
}
